/**
 * Rideshare Service
 * Generates deep links for Uber and Lyft with pickup and dropoff locations
 */

/**
 * Generate Uber deep link
 * @param {string} pickupLat - Pickup latitude
 * @param {string} pickupLng - Pickup longitude
 * @param {string} pickupNickname - Pickup location name
 * @param {string} dropoffLat - Dropoff latitude
 * @param {string} dropoffLng - Dropoff longitude
 * @param {string} dropoffNickname - Dropoff location name
 * @returns {string} Uber deep link URL
 */
export function generateUberLink(pickupLat, pickupLng, pickupNickname, dropoffLat, dropoffLng, dropoffNickname) {
  // Uber deep link format:
  // uber://?action=setPickup&pickup[latitude]=LAT&pickup[longitude]=LNG&pickup[nickname]=NAME&dropoff[latitude]=LAT&dropoff[longitude]=LNG&dropoff[nickname]=NAME
  
  const params = new URLSearchParams({
    'action': 'setPickup',
    'pickup[latitude]': pickupLat,
    'pickup[longitude]': pickupLng,
    'pickup[nickname]': pickupNickname,
    'dropoff[latitude]': dropoffLat,
    'dropoff[longitude]': dropoffLng,
    'dropoff[nickname]': dropoffNickname,
    'client_id': 'travelflow' // Optional: Your Uber client ID
  });
  
  return `uber://?${params.toString()}`;
}

/**
 * Generate Uber web fallback link
 * @param {string} pickupLat - Pickup latitude
 * @param {string} pickupLng - Pickup longitude
 * @param {string} dropoffLat - Dropoff latitude
 * @param {string} dropoffLng - Dropoff longitude
 * @returns {string} Uber web URL
 */
export function generateUberWebLink(pickupLat, pickupLng, dropoffLat, dropoffLng) {
  return `https://m.uber.com/ul/?action=setPickup&pickup[latitude]=${pickupLat}&pickup[longitude]=${pickupLng}&dropoff[latitude]=${dropoffLat}&dropoff[longitude]=${dropoffLng}`;
}

/**
 * Generate Lyft deep link
 * @param {string} pickupLat - Pickup latitude
 * @param {string} pickupLng - Pickup longitude
 * @param {string} dropoffLat - Dropoff latitude
 * @param {string} dropoffLng - Dropoff longitude
 * @returns {string} Lyft deep link URL
 */
export function generateLyftLink(pickupLat, pickupLng, dropoffLat, dropoffLng) {
  // Lyft deep link format:
  // lyft://ridetype?id=lyft&pickup[latitude]=LAT&pickup[longitude]=LNG&destination[latitude]=LAT&destination[longitude]=LNG
  
  const params = new URLSearchParams({
    'id': 'lyft',
    'pickup[latitude]': pickupLat,
    'pickup[longitude]': pickupLng,
    'destination[latitude]': dropoffLat,
    'destination[longitude]': dropoffLng
  });
  
  return `lyft://ridetype?${params.toString()}`;
}

/**
 * Generate Lyft web fallback link
 * @param {string} pickupLat - Pickup latitude
 * @param {string} pickupLng - Pickup longitude
 * @param {string} dropoffLat - Dropoff latitude
 * @param {string} dropoffLng - Dropoff longitude
 * @returns {string} Lyft web URL
 */
export function generateLyftWebLink(pickupLat, pickupLng, dropoffLat, dropoffLng) {
  return `https://lyft.com/ride?pickup[latitude]=${pickupLat}&pickup[longitude]=${pickupLng}&destination[latitude]=${dropoffLat}&destination[longitude]=${dropoffLng}`;
}

/**
 * Get airport coordinates by airport code
 * This is a simple lookup - in production, you might want to use an API
 */
export const AIRPORT_COORDINATES = {
  'DFW': { lat: 32.8968, lng: -97.0380, name: 'Dallas/Fort Worth International Airport' },
  'LGA': { lat: 40.7769, lng: -73.8740, name: 'LaGuardia Airport' },
  'JFK': { lat: 40.6413, lng: -73.7781, name: 'JFK International Airport' },
  'EWR': { lat: 40.6895, lng: -74.1745, name: 'Newark Liberty International Airport' },
  'LAX': { lat: 33.9416, lng: -118.4085, name: 'Los Angeles International Airport' },
  'ORD': { lat: 41.9742, lng: -87.9073, name: "O'Hare International Airport" },
  'ATL': { lat: 33.6407, lng: -84.4277, name: 'Hartsfield-Jackson Atlanta International Airport' },
  'MIA': { lat: 25.7959, lng: -80.2870, name: 'Miami International Airport' },
  'LHR': { lat: 51.4700, lng: -0.4543, name: 'London Heathrow Airport' },
  'CDG': { lat: 49.0097, lng: 2.5479, name: 'Paris Charles de Gaulle Airport' },
  'FCO': { lat: 41.8003, lng: 12.2389, name: 'Rome Fiumicino Airport' },
  'KDFW': { lat: 32.8968, lng: -97.0380, name: 'Dallas/Fort Worth International Airport' },
  'KLGA': { lat: 40.7769, lng: -73.8740, name: 'LaGuardia Airport' },
  'KJFK': { lat: 40.6413, lng: -73.7781, name: 'JFK International Airport' },
  'KEWR': { lat: 40.6895, lng: -74.1745, name: 'Newark Liberty International Airport' },
  'KBOS': { lat: 42.3656, lng: -71.0096, name: 'Boston Logan International Airport' }
};

/**
 * Get coordinates for an airport
 * @param {string} airportCode - IATA or ICAO airport code
 * @returns {Object|null} Airport coordinates and name
 */
export function getAirportCoordinates(airportCode) {
  if (!airportCode) return null;
  
  // Try exact match first
  if (AIRPORT_COORDINATES[airportCode]) {
    return AIRPORT_COORDINATES[airportCode];
  }
  
  // Try removing K prefix for US airports
  const withoutK = airportCode.replace(/^K/, '');
  if (AIRPORT_COORDINATES[withoutK]) {
    return AIRPORT_COORDINATES[withoutK];
  }
  
  return null;
}

/**
 * Geocode hotel address to get coordinates
 * Uses Google Maps Geocoding API via Lambda proxy
 * @param {string} hotelAddress - Full hotel address
 * @param {string} hotelName - Hotel name for logging
 * @returns {Promise<Object|null>} { lat, lng, name } or null if failed
 */
export async function getHotelCoordinates(hotelAddress, hotelName) {
  // Import geocoding service dynamically to avoid circular dependencies
  const { geocodeAddress } = await import('./geocodingService.js');
  return await geocodeAddress(hotelAddress, hotelName);
}

/**
 * Try to extract coordinates from hotel object if available
 * Returns a Promise since it may need to geocode
 * @param {Object} hotel - Hotel object with name and address
 * @returns {Promise<Object|null>} { lat, lng, name } or null if failed
 */
export async function extractHotelCoordinates(hotel) {
  // Check if coordinates are already stored
  if (hotel.latitude && hotel.longitude) {
    return {
      lat: hotel.latitude,
      lng: hotel.longitude,
      name: hotel.name || hotel.hotelName || 'Hotel'
    };
  }
  
  // Try to geocode from address
  return await getHotelCoordinates(hotel.address, hotel.name || hotel.hotelName);
}

/**
 * Get user's current location using browser geolocation API
 * @param {Object} options - Geolocation options (timeout, maximumAge, enableHighAccuracy)
 * @returns {Promise<Object>} { lat, lng } or throws error
 */
export function getCurrentLocation(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const defaultOptions = {
      timeout: 10000,
      maximumAge: 60000, // Cache for 1 minute
      enableHighAccuracy: true
    };

    const geolocationOptions = { ...defaultOptions, ...options };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        let errorMessage = 'Could not get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions for this site in your browser settings, then reload the page.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please try again.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        reject(new Error(errorMessage));
      },
      geolocationOptions
    );
  });
}

/**
 * Generate Uber deep link with current location as pickup
 * @param {number} dropoffLat - Dropoff latitude
 * @param {number} dropoffLng - Dropoff longitude
 * @param {string} dropoffName - Dropoff location name
 * @param {number} pickupLat - Current location latitude
 * @param {number} pickupLng - Current location longitude
 * @returns {string} Uber deep link URL
 */
export function generateUberLinkFromCurrentLocation(dropoffLat, dropoffLng, dropoffName, pickupLat, pickupLng) {
  return generateUberLink(
    pickupLat.toString(),
    pickupLng.toString(),
    'Current Location',
    dropoffLat.toString(),
    dropoffLng.toString(),
    dropoffName
  );
}

/**
 * Request Uber to a destination from current location
 * @param {string} dropoffAddress - Destination address
 * @param {string} dropoffName - Destination name (hotel or restaurant name)
 * @returns {Promise<void>} Resolves when Uber link is opened
 */
export async function requestUberToLocation(dropoffAddress, dropoffName) {
  try {
    // Step 1: Get current location
    const currentLocation = await getCurrentLocation();
    
    // Step 2: Geocode destination address
    const { geocodeAddress } = await import('./geocodingService.js');
    const dropoffCoords = await geocodeAddress(dropoffAddress, dropoffName);
    
    if (!dropoffCoords) {
      throw new Error('Could not find address. Please check the address.');
    }

    // Step 3: Generate Uber deep link
    const deepLink = generateUberLinkFromCurrentLocation(
      dropoffCoords.lat,
      dropoffCoords.lng,
      dropoffName || dropoffCoords.name,
      currentLocation.lat,
      currentLocation.lng
    );

    // Step 4: Generate web fallback
    const webLink = generateUberWebLink(
      currentLocation.lat,
      currentLocation.lng,
      dropoffCoords.lat,
      dropoffCoords.lng
    );

    // Step 5: Try to open Uber app
    window.location.href = deepLink;

    // Step 6: Fallback to web after delay if app doesn't open
    setTimeout(() => {
      window.open(webLink, '_blank');
    }, 1500);

  } catch (error) {
    console.error('Error requesting Uber:', error);
    throw error;
  }
}

export default {
  generateUberLink,
  generateUberWebLink,
  generateLyftLink,
  generateLyftWebLink,
  getAirportCoordinates,
  getHotelCoordinates,
  extractHotelCoordinates,
  getCurrentLocation,
  generateUberLinkFromCurrentLocation,
  requestUberToLocation,
  AIRPORT_COORDINATES
};

